import { createClient } from '@supabase/supabase-js';

const URL = process.env.REACT_APP_SUPABASE_URL;
const KEY = process.env.REACT_APP_SUPABASE_KEY;

// ── Indicador global de actividad ──────────────────────────────
// Cuenta cuántas peticiones a la base están EN VUELO. Todo el tráfico de
// supabase-js (fetchAll/insert/update, supabase.from directo en los módulos,
// storage y auth) pasa por este `fetch`, así que el contador refleja cualquier
// operación en curso. Un componente (GlobalBusy) se suscribe y muestra la barra.
let _busy = 0;
const _busyListeners = new Set();
function _setBusy(delta) {
  _busy = Math.max(0, _busy + delta);
  _busyListeners.forEach(l => { try { l(_busy); } catch { /* noop */ } });
}
export function onBusyChange(cb) {
  _busyListeners.add(cb);
  cb(_busy);
  return () => _busyListeners.delete(cb);
}
export function isBusy() { return _busy > 0; }

const _busyFetch = (...args) => {
  _setBusy(1);
  return fetch(...args).finally(() => _setBusy(-1));
};

export const supabase = createClient(URL, KEY, { global: { fetch: _busyFetch } });

// ============================================
// API de compatibilidad: mantiene la misma firma
// que el viejo engine.js (query, insert, update, remove)
// pero traduce a operaciones de Supabase
// ============================================

export async function initDB() {
  // Verifica que el cliente esté configurado SIN leer ninguna tabla: esto corre
  // antes de iniciar sesión, y sin sesión la base no debe entregar datos.
  if (!URL || !KEY) throw new Error('Faltan REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_KEY');
  const { error } = await supabase.auth.getSession();
  if (error) {
    console.error('Error conectando a Supabase:', error);
    throw error;
  }
  return true;
}

// Función genérica para "queries" simples por tabla
// Uso: await fetchAll('clientes', { orderBy: 'nombre' })
export async function fetchAll(tabla, opts = {}) {
  let q = supabase.from(tabla).select(opts.select || '*');
  if (opts.filter) {
    Object.entries(opts.filter).forEach(([col, val]) => {
      q = q.eq(col, val);
    });
  }
  if (opts.search) {
    const { col, term } = opts.search;
    q = q.ilike(col, `%${term}%`);
  }
  if (opts.orderBy) {
    q = q.order(opts.orderBy, { ascending: opts.ascending !== false });
  }
  if (opts.limit) q = q.limit(opts.limit);
  const { data, error } = await q;
  if (error) {
    console.error(`Error fetchAll(${tabla}):`, error);
    return [];
  }
  return data || [];
}

export async function fetchOne(tabla, id) {
  const { data, error } = await supabase.from(tabla).select('*').eq('id', id).maybeSingle();
  if (error) {
    console.error(`Error fetchOne(${tabla}, ${id}):`, error);
    return null;
  }
  return data;
}

export async function insertRow(tabla, payload) {
  const { data, error } = await supabase.from(tabla).insert(payload).select().single();
  if (error) {
    console.error(`Error insert(${tabla}):`, error);
    throw error;
  }
  return data;
}

export async function updateRow(tabla, id, payload) {
  const { data, error } = await supabase.from(tabla).update(payload).eq('id', id).select().single();
  if (error) {
    console.error(`Error update(${tabla}, ${id}):`, error);
    throw error;
  }
  return data;
}

export async function deleteRow(tabla, id) {
  const { error } = await supabase.from(tabla).delete().eq('id', id);
  if (error) {
    console.error(`Error delete(${tabla}, ${id}):`, error);
    throw error;
  }
  return true;
}

// Conteos rápidos
export async function count(tabla, filter = {}) {
  let q = supabase.from(tabla).select('*', { count: 'exact', head: true });
  Object.entries(filter).forEach(([col, val]) => {
    q = q.eq(col, val);
  });
  const { count: c, error } = await q;
  if (error) return 0;
  return c || 0;
}

// Sumas
export async function sum(tabla, columna, filter = {}) {
  let q = supabase.from(tabla).select(columna);
  Object.entries(filter).forEach(([col, val]) => {
    q = q.eq(col, val);
  });
  const { data, error } = await q;
  if (error) return 0;
  return (data || []).reduce((acc, r) => acc + (Number(r[columna]) || 0), 0);
}

// ============================================
// Niveles de límite de Auto (LC / DPA / GM)
// ============================================
// Tolerante: si la tabla aún no existe en este entorno, devuelve []
// y el selector simplemente no se muestra (no rompe el formulario).
export async function fetchAutoLimiteOpciones() {
  try {
    const { data, error } = await supabase
      .from('auto_limite_opciones')
      .select('*')
      .eq('activo', true)
      .order('tipo')
      .order('orden');
    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}

// ============================================
// Referidos (referidores) — catálogo de quienes pasan pólizas
// ============================================
// Tolerante: si la tabla aún no existe en este entorno, devuelve []
// y el selector simplemente no se muestra (no rompe el formulario).
export async function fetchReferidos() {
  try {
    const { data, error } = await supabase
      .from('referidos')
      .select('id, nombre, comision_pct, comision_nueva_pct, comision_renovacion_pct')
      .eq('activo', true)
      .order('nombre');
    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}

// ============================================
// Registro Vehicular (RUV) — tipo de documento garantizado
// ============================================
// Devuelve el id del tipo de documento "Registro Vehicular (RUV)".
// Si no existe en este entorno, lo crea con categoría 'poliza' para que
// aparezca en los Documentos de la póliza (el DocManager de la póliza filtra
// por categoría y NO incluye 'auto'). Tolerante a carreras y a fallos.
export async function ensureTipoDocRUV() {
  try {
    const { data: existing } = await supabase
      .from('tipos_documento')
      .select('id')
      .or('codigo.eq.RUV,nombre.ilike.%registro vehicular%')
      .limit(1);
    if (existing && existing.length) return existing[0].id;
    try {
      const row = await insertRow('tipos_documento', {
        nombre: 'Registro Vehicular (RUV)',
        codigo: 'RUV',
        categoria: 'poliza',
        requiere_anio: false,
        activo: true,
      });
      return row.id;
    } catch {
      // Posible carrera: si otro proceso lo creó en paralelo, re-buscar.
      const { data: again } = await supabase
        .from('tipos_documento').select('id').eq('codigo', 'RUV').limit(1);
      return (again && again.length) ? again[0].id : null;
    }
  } catch {
    return null;
  }
}

// Sube el registro vehicular (RUV) al storage y lo registra en `documentos`
// enlazado a la póliza (y al cliente si se conoce), para que quede visible en
// la pestaña Documentos de la póliza. Devuelve el registro insertado.
export async function subirRegistroVehicular(polizaId, clienteId, file) {
  const tipoId = await ensureTipoDocRUV();
  const ext  = (file.name.split('.').pop() || 'pdf').toLowerCase();
  const path = `documentos/poliza/${polizaId}/${Date.now()}.${ext}`;
  const { error: upErr } = await supabase.storage
    .from('solicitud-archivos')
    .upload(path, file, { upsert: false });
  if (upErr) throw upErr;
  return insertRow('documentos', {
    tipo_documento_id: tipoId,
    nombre_archivo: file.name,
    storage_path: path,
    activo: true,
    poliza_id: polizaId,
    ...(clienteId ? { cliente_id: clienteId } : {}),
  });
}

// Devuelve un mapa { cobertura_id: descripcion } para un set de ids del catálogo.
// Tolerante: si falla, devuelve {} y el texto simplemente no se muestra.
export async function fetchCoberturaDescripciones(ids = []) {
  const clean = [...new Set((ids || []).filter(Boolean))];
  if (clean.length === 0) return {};
  try {
    const { data, error } = await supabase
      .from('coberturas')
      .select('id, descripcion')
      .in('id', clean);
    if (error) return {};
    const map = {};
    (data || []).forEach(c => { map[c.id] = c.descripcion || ''; });
    return map;
  } catch {
    return {};
  }
}

// ============================================
// Documentos de clientes (Storage + DB)
// ============================================

export async function uploadClienteDoc(clienteId, file, tipo, comentario) {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${clienteId}/${tipo}_${Date.now()}_${safeName}`;

  const { error: uploadError } = await supabase.storage
    .from('cliente-docs')
    .upload(path, file, { upsert: false });
  if (uploadError) throw uploadError;

  return insertRow('cliente_documentos', {
    cliente_id: clienteId,
    tipo_doc: tipo,
    nombre_archivo: file.name,
    storage_path: path,
    comentario: comentario || null,
  });
}

export async function fetchClienteDocs(clienteId) {
  const { data, error } = await supabase
    .from('cliente_documentos')
    .select('*')
    .eq('cliente_id', clienteId)
    .order('subido_en', { ascending: false });
  if (error) return [];
  return data || [];
}

export async function deleteClienteDoc(docId, storagePath) {
  await supabase.storage.from('cliente-docs').remove([storagePath]);
  return deleteRow('cliente_documentos', docId);
}

export function getClienteDocUrl(storagePath) {
  const { data } = supabase.storage.from('cliente-docs').getPublicUrl(storagePath);
  return data?.publicUrl || null;
}

// ── Autenticación (Supabase Auth) ─────────────────────────────
// Las contraseñas viven cifradas en `auth.users`, que la aplicación nunca lee.
// La sesión es un token firmado por Supabase: no se puede falsificar desde el
// navegador, y viaja en cada petición para que RLS sepa quién pregunta.

// Ficha de perfil (rol, permisos, vínculos) del usuario autenticado.
export async function fetchPerfil(authUserId) {
  if (!authUserId) return null;
  const { data } = await supabase
    .from('usuarios')
    .select('*, roles(*)')
    .eq('auth_user_id', authUserId)
    .eq('activo', true)
    .maybeSingle();
  return data || null;
}

export async function loginUser(login, password) {
  const ident = (login || '').trim();
  if (!ident || !password) return null;

  // Supabase Auth solo entiende correos. Si escribieron el usuario corto
  // (ej. "rgriffith"), lo traducimos con una función de la base que solo
  // devuelve el correo — no expone la tabla de usuarios.
  let email = ident;
  if (!ident.includes('@')) {
    const { data: correo, error } = await supabase.rpc('email_de_usuario', { p_login: ident });
    if (error || !correo) return null;
    email = correo;
  }

  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error || !data?.user) return null;

  const perfil = await fetchPerfil(data.user.id);
  if (!perfil) {
    // Tiene credenciales válidas pero no ficha activa: no debe quedar dentro.
    await supabase.auth.signOut();
    return null;
  }
  return perfil;
}

export async function logoutUser() {
  await supabase.auth.signOut();
}

// Cambia la contraseña del usuario actual, verificando primero la anterior.
export async function cambiarPassword(email, passwordActual, passwordNueva) {
  const { error: errVerif } = await supabase.auth.signInWithPassword({ email, password: passwordActual });
  if (errVerif) return { ok: false, error: 'La contraseña actual no es correcta.' };
  const { error } = await supabase.auth.updateUser({ password: passwordNueva });
  if (error) return { ok: false, error: error.message };
  await limpiarDebeCambiar();
  return { ok: true };
}

// Cambio OBLIGATORIO del primer ingreso: el usuario ya está autenticado (con la
// clave temporal), solo define una nueva. No pide la anterior porque acaba de usarla.
export async function establecerPasswordPropia(passwordNueva) {
  const { error } = await supabase.auth.updateUser({ password: passwordNueva });
  if (error) return { ok: false, error: error.message };
  await limpiarDebeCambiar();
  return { ok: true };
}

// Apaga la bandera "debe_cambiar_password" de la propia ficha.
async function limpiarDebeCambiar() {
  const { data: { user } } = await supabase.auth.getUser();
  if (user) await supabase.from('usuarios').update({ debe_cambiar_password: false }).eq('auth_user_id', user.id);
}
